import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto p-3 rounded-full bg-destructive/10 text-destructive w-fit mb-4">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <CardTitle className="text-xl">เกิดข้อผิดพลาด</CardTitle>
          <CardDescription>
            ขออภัย มีบางอย่างผิดพลาด ทีมงานได้รับแจ้งเรียบร้อยแล้ว
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {import.meta.env.DEV && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs font-mono text-muted-foreground break-all">
                {error.message}
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={() => window.location.href = "/"}
            >
              <Home className="h-4 w-4" />
              หน้าหลัก
            </Button>
            <Button
              variant="default"
              className="flex-1 gap-2"
              onClick={resetError}
            >
              <RefreshCw className="h-4 w-4" />
              ลองใหม่
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error, componentStack) => {
        console.error("ErrorBoundary caught error:", error);
        console.error("Component stack:", componentStack);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default ErrorBoundary;
